import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button, Icon, Spinner, Tooltip } from '@components';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { useSendManualPaymentLinkCustomTermMutation } from '@features/group-management/queries/GroupQuery';
import useNotify from '@hooks/notify';
import { ApiError, PaymentOrderTermType } from '@types';
import { formatISODate } from '@utils/date';
import { getLocalStorageItem } from '@utils/storage';

import TermBreakdownTooltip, {
  shouldShowTermInstallmentAmountTooltip
} from './TermInstallmentBreakdownTooltip';

import theme from '../../../../../themes/colors';

const DATE_FMT = 'DD MMM YYYY' as const;

type InstallmentScheduleBlockProps = {
  rows: PaymentOrderTermType[];
  memberId?: string;
  isDefaultCurrency: boolean;
  source: 'member-details' | 'transaction-history';
};

const paymentStatus = (row: PaymentOrderTermType) =>
  row.payment_status?.toLowerCase() || '';

const termStatus = (row: PaymentOrderTermType) =>
  row.status?.toLowerCase() || '';

const isPaidComplete = (row: PaymentOrderTermType) => {
  const status = paymentStatus(row);
  return status === 'success';
};

const isTermInactive = (
  row: PaymentOrderTermType,
  paymentLinkNotGenerated: boolean
) => !paymentLinkNotGenerated && termStatus(row) === 'inactive';

const isTermDisabled = (row: PaymentOrderTermType) =>
  paymentStatus(row) === 'disabled';

const isPaymentLinkNotGenerated = (row: PaymentOrderTermType) => {
  if (paymentStatus(row) !== 'payment_order_pending' || !row.due_date) {
    return false;
  }
  return (
    formatISODate({
      date: row.due_date,
      format: 'yyyy-mm-dd',
      timeZone: 'Asia/Kolkata'
    }) <
    formatISODate({
      date: new Date(),
      format: 'yyyy-mm-dd',
      timeZone: 'Asia/Kolkata'
    })
  );
};

const disabledTermTooltipText = (row: PaymentOrderTermType) => {
  const raw = (row.disabled_at ?? '').trim();
  if (!raw) return 'Payment link disabled';
  return `Payment link disabled on ${formatISODate({
    date: raw,
    format: DATE_FMT
  })}`;
};

const nonZeroAmount = (v: unknown) => Number(v ?? 0) !== 0;

const isProcessing = (row: PaymentOrderTermType) =>
  paymentStatus(row) === 'processing';

const statusTooltipText = (
  row: PaymentOrderTermType,
  paymentLinkNotGenerated: boolean
) => {
  if (isPaidComplete(row)) {
    return { icon: 'check-circle-broken-green', text: 'Payment success' };
  }
  if (paymentLinkNotGenerated) {
    return {
      icon: 'unlink-red',
      text: 'Payment link not generated'
    };
  }
  if (isTermInactive(row, paymentLinkNotGenerated)) {
    return { icon: 'clock-check', text: 'The term is inactive' };
  }
  if (isTermDisabled(row)) {
    return { icon: 'slash-circle', text: disabledTermTooltipText(row) };
  }
  if (isProcessing(row)) {
    return { icon: 'check-circle-broken-orange', text: 'Pending payment' };
  }
  return {
    icon: 'clock-check',
    text: 'Payment scheduled'
  };
};

const termDisplayName = (
  name: string | undefined,
  index: number,
  termNumber?: number
) => {
  const raw = (name ?? '').trim();
  if (raw) return raw;
  const displayTermNumber = termNumber ?? index + 1;
  return `Installment ${displayTermNumber}`;
};
const segmentConnectorClass = (row: PaymentOrderTermType) =>
  isPaidComplete(row) ? 'border-cofeeGreen7' : 'border-grey40';

const StatusIcon = ({
  row,
  paymentLinkNotGenerated
}: {
  row: PaymentOrderTermType;
  paymentLinkNotGenerated: boolean;
}) => {
  if (isPaidComplete(row)) {
    return (
      <Icon
        name="rounded-green-tick-with-bg"
        className="size-8 gap-[7.35px] rounded-full p-[1.47px] opacity-100"
      />
    );
  }
  if (paymentLinkNotGenerated) {
    return (
      <Icon
        name="rounded-unlink-with-bg"
        className="size-8 gap-[7.35px] rounded-full p-[1.47px] opacity-100"
      />
    );
  }
  if (isTermInactive(row, paymentLinkNotGenerated)) {
    return (
      <Icon
        name="rounded-scheduled-timer-with-bg"
        className="size-8 gap-[7.35px] rounded-full p-[1.47px] opacity-100"
      />
    );
  }
  if (isTermDisabled(row)) {
    return (
      <Icon
        name="disabled-with-bg"
        className="size-8 gap-[7.35px] rounded-full p-[1.47px] opacity-100"
      />
    );
  }
  if (isProcessing(row)) {
    return (
      <Icon
        name="rounded-orange-tick-with-bg"
        className="size-8 gap-[7.35px] rounded-full p-[1.47px] opacity-100"
      />
    );
  }
  return (
    <Icon
      name="rounded-scheduled-timer-with-bg"
      className="size-8 gap-[7.35px] rounded-full p-[1.47px] opacity-100"
    />
  );
};

const DueDateLine = ({
  date,
  paragraphClassName
}: {
  date: string;
  paragraphClassName: string;
}) => (
  <p className={paragraphClassName}>
    Due on {formatISODate({ date, format: DATE_FMT })}
  </p>
);

const TermTimelineColumn = ({
  row,
  index,
  rows,
  paymentLinkNotGenerated
}: {
  row: PaymentOrderTermType;
  index: number;
  rows: PaymentOrderTermType[];
  paymentLinkNotGenerated: boolean;
}) => (
  <div className="flex w-6 shrink-0 flex-col items-center self-stretch">
    {index > 0 ? (
      <div
        className={`h-4 w-0 shrink-0 border-l-2 border-dashed ${segmentConnectorClass(
          rows[index - 1]
        )}`}
      />
    ) : null}
    <StatusIcon row={row} paymentLinkNotGenerated={paymentLinkNotGenerated} />
    {index < rows.length - 1 ? (
      <div
        className={`mt-0 min-h-10 w-0 flex-1 border-l-2 border-dashed ${segmentConnectorClass(
          row
        )}`}
      />
    ) : null}
  </div>
);

const TermCardAmount = ({
  row,
  memberId
}: {
  row: PaymentOrderTermType;
  memberId: string;
}) => {
  const { groupId } = useParams();
  const [isLinkSent, setIsLinkSent] = useState(false);
  const { createErrorAlert } = useNotify();

  const [sendManualPaymentLinkCustomTerm, { isLoading: isSendingLink }] =
    useSendManualPaymentLinkCustomTermMutation();

  const handleSendPaymentLink = async () => {
    const res = await sendManualPaymentLinkCustomTerm({
      params: {
        organizationId: getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID),
        branchId: getLocalStorageItem(LOCAL_STORAGE_KEYS.BRANCH_ID),
        groupId: groupId ?? '',
        memberId
      }
    });
    if ('data' in res) {
      setIsLinkSent(true);
    } else if ('error' in res) {
      const apiError = res.error as ApiError;
      createErrorAlert(
        apiError.data?.message || 'Error in fetching member data'
      );
    }
  };
  if (isPaidComplete(row)) {
    return row.paid_on ? (
      <p className="mt-2 text-sm font-normal text-grey">
        Paid on {formatISODate({ date: row.paid_on, format: DATE_FMT })}
      </p>
    ) : null;
  }

  const status = paymentStatus(row);

  const showPaidPendingSplit =
    status === 'processing' &&
    nonZeroAmount(row.paid_amount) &&
    nonZeroAmount(row.pending_amount);

  const showSendPaymentLinkButton = row.can_send_payment_link;

  if (
    !row.due_date &&
    status !== 'processing' &&
    status !== 'payment_order_pending'
  ) {
    return null;
  }

  return (
    <div className="space-y-1">
      {row.due_date && (
        <DueDateLine
          date={row.due_date}
          paragraphClassName={
            isTermDisabled(row)
              ? 'text-sm font-normal text-grey line-through'
              : 'text-sm font-normal text-grey'
          }
        />
      )}
      {showPaidPendingSplit && (
        <p className="flex flex-wrap items-center gap-x-2 text-xs">
          <span className="inline-flex items-center gap-0.5 font-medium text-cofeeGreen7">
            Paid:
            <CurrencyAmount
              value={Number(row.paid_amount ?? 0)}
              className="gap-0.25"
              symbolClass="!text-xs text-cofeeGreen7"
              valueClass="font-medium text-cofeeGreen7"
            />
          </span>
          <span className="text-grey40">|</span>
          <span className="inline-flex items-center gap-0.5 font-medium text-cofeeOrange6">
            Pending:
            <CurrencyAmount
              value={Number(row.pending_amount ?? 0)}
              className="gap-0.25"
              symbolClass="!text-xs text-cofeeOrange6"
              valueClass="font-medium text-cofeeOrange6"
            />
          </span>
        </p>
      )}

      {isLinkSent ? (
        <div className="ml-auto flex w-47 items-center justify-end gap-1">
          <Icon name="tick" stroke={theme.cofeePrimary7} />
          <span className="text-sm font-medium text-cofeeGreen7">
            Payment link sent
          </span>
        </div>
      ) : (
        showSendPaymentLinkButton &&
        (isSendingLink ? (
          <div className="ml-auto flex w-47 items-center justify-center">
            <Spinner className="size-4 border border-cofeePrimary6" />
            <span className="text-sm font-medium text-cofeePrimary6">
              Sending payment link
            </span>
          </div>
        ) : (
          <Button
            variant="secondary"
            size="small"
            leftIconName="send-link-green"
            label="Send payment link"
            labelStyle="!text-cofeePrimary6 !text-sm !font-medium !text-center !align-middle"
            className="ml-auto h-9 w-47"
            handleClick={handleSendPaymentLink}
          />
        ))
      )}
    </div>
  );
};

const TermCard = ({
  row,
  index,
  memberId,
  amountSymbolClass,
  isDefaultCurrency,
  onAmountHoverChange,
  paymentLinkNotGenerated
}: {
  row: PaymentOrderTermType;
  index: number;
  memberId: string;
  amountSymbolClass: string;
  isDefaultCurrency: boolean;
  onAmountHoverChange: (hovered: boolean) => void;
  paymentLinkNotGenerated: boolean;
}) => {
  const requestedTermTotal = Number(row.amount ?? 0);
  const disabled = isTermDisabled(row);

  const rowClass = disabled
    ? 'font-normal text-cofeeNeutral8 line-through'
    : 'font-semibold text-content';
  const amountSymbolClassResolved = disabled
    ? `${amountSymbolClass} text-cofeeNeutral8 line-through`.trim()
    : amountSymbolClass;

  const amountDisplay = (
    <CurrencyAmount
      value={requestedTermTotal}
      className="justify-end gap-0.5"
      valueClass={rowClass}
      symbolClass={amountSymbolClassResolved}
    />
  );

  const showBreakdownTooltip = shouldShowTermInstallmentAmountTooltip(
    row,
    String(row?.amount ?? 0)
  );

  const cardInner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className={rowClass}>
          {termDisplayName(row.name, index, row.term_number)}
        </div>
        <div
          className="flex shrink-0 items-start gap-1.5"
          onMouseEnter={() => onAmountHoverChange(true)}
          onMouseLeave={() => onAmountHoverChange(false)}
        >
          {showBreakdownTooltip && !disabled ? (
            <TermBreakdownTooltip
              id={`installment-schedule-total-${index}`}
              row={row}
              isDefaultCurrency={isDefaultCurrency}
            >
              <span className="shrink-0 border-b border-dotted border-cofeeNeutral10">
                {amountDisplay}
              </span>
            </TermBreakdownTooltip>
          ) : (
            <span className="shrink-0">{amountDisplay}</span>
          )}
        </div>
      </div>
      <TermCardAmount row={row} memberId={memberId} />
    </>
  );

  return (
    <div
      className={`rounded-lg border border-grey40 p-4 ${
        disabled || paymentLinkNotGenerated
          ? 'cursor-not-allowed bg-cofeeGreyBlue1'
          : 'bg-white'
      }`}
    >
      {cardInner}
    </div>
  );
};

const TermRow = ({
  row,
  index,
  rows,
  memberId,
  amountSymbolClass,
  isDefaultCurrency,
  paymentLinkNotGenerated
}: {
  row: PaymentOrderTermType;
  index: number;
  rows: PaymentOrderTermType[];
  memberId: string;
  amountSymbolClass: string;
  isDefaultCurrency: boolean;
  paymentLinkNotGenerated: boolean;
}) => {
  const [isAmountHovered, setIsAmountHovered] = useState(false);

  const disabled = isTermDisabled(row);

  const termKey = row.term_number ?? index;
  const statusTooltipId = `installment-status-${index}-${termKey}`;

  const showBreakdownTooltip = shouldShowTermInstallmentAmountTooltip(
    row,
    String(row?.amount ?? 0)
  );

  const showStatusTooltip =
    disabled || !(showBreakdownTooltip && isAmountHovered);
  const rowTooltipId = showStatusTooltip ? statusTooltipId : undefined;
  const statusTooltip = statusTooltipText(row, paymentLinkNotGenerated);

  return (
    <>
      <div className="flex gap-3" data-tooltip-id={rowTooltipId}>
        <TermTimelineColumn
          row={row}
          index={index}
          rows={rows}
          paymentLinkNotGenerated={paymentLinkNotGenerated}
        />
        <div className="min-w-0 flex-1 pb-5">
          <TermCard
            row={row}
            index={index}
            memberId={memberId ?? ''}
            amountSymbolClass={amountSymbolClass}
            isDefaultCurrency={isDefaultCurrency}
            onAmountHoverChange={setIsAmountHovered}
            paymentLinkNotGenerated={paymentLinkNotGenerated}
          />
        </div>
      </div>

      {showStatusTooltip ? (
        <Tooltip
          id={statusTooltipId}
          type="custom"
          variant="dark"
          place="top"
          className="!max-w-90"
        >
          <div className="flex items-start gap-2 p-1">
            <Icon
              name={statusTooltip.icon}
              size="medium"
              className="shrink-0"
            />
            <span className="text-sm font-normal">{statusTooltip.text}</span>
          </div>
        </Tooltip>
      ) : null}
    </>
  );
};

const InstallmentScheduleBlock = ({
  rows,
  memberId,
  isDefaultCurrency,
  source
}: InstallmentScheduleBlockProps) => {
  if (!rows.length) return null;

  const amountSymbolClass = isDefaultCurrency ? '' : '!text-sm !font-medium';
  let paymentLinkTermFound = false;
  return (
    <div className="mt-4">
      {source === 'member-details' && (
        <div className="text-md mb-3 font-medium text-cofeeNeutral10">
          Installment schedule
        </div>
      )}
      <div className="flex flex-col">
        {rows.map((row, index) => {
          const paymentLinkNotGenerated =
            isPaymentLinkNotGenerated(row) ||
            (!paymentLinkTermFound && Number(row.amount ?? 0) === 0);

          if (
            row.can_send_payment_link ||
            paymentStatus(row) === 'processing'
          ) {
            paymentLinkTermFound = true;
          }

          return (
            <TermRow
              key={`${row.term_id ?? ''}-${row.due_date}`}
              row={row}
              index={index}
              rows={rows}
              memberId={memberId ?? ''}
              amountSymbolClass={amountSymbolClass}
              isDefaultCurrency={isDefaultCurrency}
              paymentLinkNotGenerated={paymentLinkNotGenerated}
            />
          );
        })}
      </div>
    </div>
  );
};

export default InstallmentScheduleBlock;
